import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { refreshToken, clearAuth } from '../../store/authSlice';

interface AuthManagerProps {
  children: React.ReactNode;
}

const AuthManager: React.FC<AuthManagerProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { token, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    if (isAuthenticated && token) {
      // Set up automatic PROACTIVE token refresh every 20 minutes
      // This prevents token expiration during long-running interviews
      // Backend tokens typically expire after 30 minutes, so we refresh at 20 minutes
      // to ensure uninterrupted service during interviews
      const REFRESH_INTERVAL = 180 * 60 * 1000; // 20 minutes
      
      console.log('🔄 [AuthManager] Setting up proactive token refresh every 118 minutes');
      
      refreshInterval = setInterval(async () => {
        try {
          console.log('🔄 [AuthManager] Proactively refreshing token...');
          await dispatch(refreshToken()).unwrap();
          console.log('✅ [AuthManager] Token refreshed successfully');
          
          // Dispatch custom event to notify components that token was refreshed
          window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
            detail: { timestamp: Date.now() } 
          }));
        } catch (error) {
          console.error('❌ [AuthManager] Token refresh failed:', error);
          // If refresh fails, clear auth state
          dispatch(clearAuth());
        }
      }, REFRESH_INTERVAL);
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [dispatch, isAuthenticated, token]);

  // Handle browser beforeunload to attempt logout
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthenticated) {
        // Note: This is best effort - the browser may not wait for the request to complete
        navigator.sendBeacon && navigator.sendBeacon('/auth/logout');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated]);

  return <>{children}</>;
};

export default AuthManager;
